import React, { useState, useCallback } from 'react';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { OfficeApp } from '../office-shared';
import { WordBody } from './WordBody';
import { WordStatusBar } from './WordStatusBar';
import WordRibbon from './WordRibbon';
import { WordSlateEditorProvider } from './WordSlateEditorContext';
import type { InitialMessage } from '../office-shared/copilot/CopilotPane';

// Function to count characters in text
const countCharacters = (text: string): number => {
  return text.length;
};

export const WordSilhouette: React.FC = () => {
  const [characterCount, setCharacterCount] = useState(0);
  const [isCopilotPaneOpen, setIsCopilotPaneOpen] = useState(false);
  const [copilotInitialMessage, setCopilotInitialMessage] = useState<InitialMessage | undefined>(undefined);

  const handleTextChange = useCallback((text: string) => {
    const count = countCharacters(text);
    setCharacterCount(count);
  }, []);

  const handleCopilotClick = useCallback(() => {
    // Clear initial message when toggling via icon
    setCopilotInitialMessage(undefined);
    setIsCopilotPaneOpen(!isCopilotPaneOpen);
  }, [isCopilotPaneOpen]);

  const handleCloseCopilotPane = useCallback(() => {
    setCopilotInitialMessage(undefined);
    setIsCopilotPaneOpen(false);
  }, []);

  // Handle Summary card click - show full summary as AI response
  const handleSummaryClick = useCallback((summary: string) => {
    setCopilotInitialMessage({
      type: 'assistant',
      content: summary
    });
    setIsCopilotPaneOpen(true);
  }, []);

  // Handle Pill click - send pill text as user message
  const handlePillClick = useCallback((pillText: string) => {
    setCopilotInitialMessage({
      type: 'user',
      content: pillText
    });
    setIsCopilotPaneOpen(true);
  }, []);

  return (
    <FluentProvider theme={webLightTheme}>
      <WordSlateEditorProvider>
        <OfficeApp 
          appName="Word"
          documentTitle="The Global Smart Living Fixtures..."
          statusBar={<WordStatusBar characterCount={characterCount} />}
          customRibbon={<WordRibbon />}
          headerFixed={true}
        >
          <WordBody 
            onTextChange={handleTextChange} 
            isCopilotPaneOpen={isCopilotPaneOpen}
            onCloseCopilotPane={handleCloseCopilotPane}
            onCopilotClick={handleCopilotClick}
            onSummaryClick={handleSummaryClick}
            onPillClick={handlePillClick}
            copilotInitialMessage={copilotInitialMessage}
          />
        </OfficeApp>
      </WordSlateEditorProvider>
    </FluentProvider>
  );
};

export default WordSilhouette;
